import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActivityTimeline = ({ activities, onActivityClick }) => {
  // Deduplicate message activities from the same sender, keeping only the latest
  const deduplicatedActivities = (() => {
    const latestMessageBySender = new Map();
    const nonMessageActivities = [];

    activities.forEach((activity) => {
      if (activity.type === 'message' && activity.metadata?.sender_id) {
        const senderId = activity.metadata.sender_id;
        const existing = latestMessageBySender.get(senderId);
        if (!existing || new Date(activity.timestamp) > new Date(existing.timestamp)) {
          latestMessageBySender.set(senderId, activity);
        }
      } else {
        nonMessageActivities.push(activity);
      }
    });

    return [...nonMessageActivities, ...latestMessageBySender.values()]
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  })();

  // Group activities by date
  const groupedActivities = deduplicatedActivities.reduce((groups, activity) => {
    const date = new Date(activity.timestamp).toLocaleDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(activity);
    return groups;
  }, {});

  // Format date for display
  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return new Intl.DateTimeFormat('en-US', { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric' 
      }).format(date);
    }
  };

  // Format time for display
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Get icon for activity type
  const getActivityIcon = (type) => {
    switch (type) {
      case 'application':
        return 'Send';
      case 'match':
        return 'Heart';
      case 'message':
        return 'MessageSquare';
      case 'profile_view':
        return 'Eye';
      case 'skill_assessment':
        return 'Award';
      case 'interview':
        return 'Video';
      case 'job_save':
        return 'Bookmark';
      case 'recommendation':
        return 'ThumbsUp';
      default:
        return 'Bell';
    }
  };

  // Get color for activity type
  const getActivityColor = (type) => {
    switch (type) {
      case 'application':
        return 'text-primary bg-primary-100';
      case 'match':
        return 'text-error bg-error-100';
      case 'message':
        return 'text-accent bg-accent-100';
      case 'profile_view':
        return 'text-success bg-success-100';
      case 'skill_assessment':
        return 'text-warning bg-warning-100';
      case 'interview':
        return 'text-secondary bg-secondary-100';
      case 'job_save':
        return 'text-blue-700 bg-blue-100';
      case 'recommendation':
        return 'text-purple-700 bg-purple-100';
      default:
        return 'text-text-secondary bg-secondary-100';
    }
  };

  // Get action button based on activity type
  const getActionButton = (activity) => {
    switch (activity.type) {
      case 'application':
        return {
          label: 'View Status',
          icon: 'ExternalLink',
        };
      case 'match':
        return {
          label: 'View Job',
          icon: 'ExternalLink',
        };
      case 'message':
        return {
          label: 'Reply',
          icon: 'MessageCircle',
        };
      case 'profile_view':
        return {
          label: 'View Profile',
          icon: 'User',
        };
      case 'interview':
        return {
          label: 'Prepare',
          icon: 'Calendar',
        };
      case 'job_save':
        return {
          label: 'View Job',
          icon: 'ExternalLink',
        };
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      {Object.keys(groupedActivities)
        .sort((a, b) => new Date(b) - new Date(a))
        .map((date) => (
          <div key={date} className="space-y-4">
            <div className="flex items-center">
              <h3 className="text-lg font-semibold text-text-primary">
                {formatDateHeader(date)}
              </h3>
              <div className="ml-4 h-px flex-1 bg-gray-200"></div>
            </div>
            
            <div className="space-y-3">
              {groupedActivities[date]
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                .map((activity) => (
                  <div 
                    key={activity.id} 
                    className="bg-white rounded-lg border border-gray-200 shadow-sm hover:border-primary-200 hover:shadow-md transition-all duration-200"
                    onClick={() => onActivityClick(activity)}
                  >
                    <div className="p-4">
                      <div className="flex items-start">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getActivityColor(activity.type)}`}>
                          <Icon name={getActivityIcon(activity.type)} size={18} />
                        </div>
                        
                        <div className="ml-3 flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium text-text-primary line-clamp-1">
                              {activity.title}
                            </h4>
                            <span className="text-xs text-text-muted ml-2 flex-shrink-0">
                              {formatTime(activity.timestamp)}
                            </span>
                          </div>
                          
                          <p className="mt-1 text-sm text-text-secondary">
                            {activity.description}
                          </p>
                          
                          {activity.metadata && (
                            <div className="flex flex-wrap items-center gap-3 mt-3">
                              {activity.metadata.company && (
                                <div className="flex items-center gap-1">
                                  <Icon name="Building" size={12} className="text-text-muted" />
                                  <span className="text-xs text-text-muted">{activity.metadata.company}</span>
                                </div>
                              )}
                              {activity.metadata.amount && (
                                <div className="flex items-center gap-1">
                                  <Icon name="DollarSign" size={12} className="text-success" />
                                  <span className="text-xs text-success font-medium">{activity.metadata.amount}</span>
                                </div>
                              )}
                              {activity.metadata.status && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  activity.metadata.status === 'accepted' ? 'bg-success-100 text-success-700' :
                                  activity.metadata.status === 'pending' ? 'bg-warning-100 text-warning-700' : 
                                  activity.metadata.status === 'rejected' ? 'bg-error-100 text-error-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {activity.metadata.status}
                                </span>
                              )}
                            </div>
                          )}
                          
                          {getActionButton(activity) && (
                            <div className="mt-3">
                              <Button
                                size="xs"
                                variant="outline"
                                iconName={getActionButton(activity).icon}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onActivityClick(activity);
                                }}
                              >
                                {getActionButton(activity).label}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ))}
    </div>
  );
};

export default ActivityTimeline;
